import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { Billboard, Line } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef, useState } from 'react';
import { Group, Quaternion } from 'three';
import { contourLines } from '../_pure/emphasisContour.js';
import { resolveFlagFlyRotation } from '../_pure/flagPostTransform.js';
const CONTOUR_LINE_PIXELS = 1.5;
const CONTOUR_FADE_RATE = 10;
const CONTOUR_VANISH = 0.01;
const CONTOUR_RENDER_ORDER = 9_000;
function ContourRing(props) {
    const { contour, present } = props.shown;
    const opacity = useRef(0);
    const lines = useRef([]);
    const circles = useMemo(() => contourLines(contour), [contour]);
    const fly = useRef(null);
    const rotation = useMemo(() => new Quaternion(), []);
    useFrame(({ camera }, delta) => {
        if (contour.flagPostOffset && fly.current) {
            resolveFlagFlyRotation(contour.flagPostOffset, camera.getWorldQuaternion(rotation), fly.current.quaternion);
        }
        const seconds = Math.min(delta, 0.1);
        opacity.current += ((present ? 1 : 0) - opacity.current) * (1 - Math.exp(-CONTOUR_FADE_RATE * seconds));
        for (const line of lines.current) {
            if (line)
                line.material.opacity = opacity.current;
        }
        if (!present && opacity.current < CONTOUR_VANISH)
            props.onGone(contour.key);
    });
    const ShapeFrame = 'sphereRadius' in contour ? Billboard : 'group';
    return (_jsx(ShapeFrame, { position: contour.position, rotation: contour.rotation, children: _jsx("group", { ref: fly, children: circles.map((points, index) => (_jsx(Line, { ref: (line) => { lines.current[index] = line; }, points: points, color: contour.color, lineWidth: CONTOUR_LINE_PIXELS, transparent: true, opacity: 0, depthWrite: false, toneMapped: false, renderOrder: CONTOUR_RENDER_ORDER }, index))) }) }));
}
/** Ring and line outlines fade in when they appear and out when their emphasis ends. */
export function EmphasisContours(props) {
    const [state, setState] = useState(() => ({
        source: props.contours,
        shown: props.contours.map(contour => ({ contour, present: true })),
    }));
    // Reconcile before committing children, not in a passive effect on every playback frame.
    if (state.source !== props.contours) {
        const current = new Set(props.contours.map(contour => contour.key));
        const leaving = state.shown.filter(entry => !current.has(entry.contour.key))
            .map(entry => ({ contour: entry.contour, present: false }));
        setState({ source: props.contours, shown: [
                ...props.contours.map(contour => ({ contour, present: true })), ...leaving,
            ] });
    }
    const onGone = useMemo(() => (key) => setState(previous => {
        const shown = previous.shown.filter(entry => entry.present || entry.contour.key !== key);
        return shown.length === previous.shown.length ? previous : { ...previous, shown };
    }), []);
    return (_jsx(_Fragment, { children: state.shown.map(entry => _jsx(ContourRing, { shown: entry, onGone: onGone }, entry.contour.key)) }));
}
